import type { LearningsStatus } from '@@/api/learnings/learnings-status';
import { Clock } from 'lucide-react';
import { cn } from 'helpers/cn';
import { MICRO_LABEL_SHAPE } from '../../ConsolePanel';
import { Section, SetupSteps, StatusPill } from './parts';

type WaitingViewProps = { status: LearningsStatus };

const PAUSED: Record<NonNullable<LearningsStatus['paused']>, string> = {
  'out-of-credits': "Paused: this account's AI credits have run out. Writing starts again once there are credits.",
  'monthly-cap': "Paused: this account has used this month's learnings allowance. Writing starts again next month.",
  'no-account': 'Paused: this website has no account to charge the AI credits to.',
};

/**
 * Learnings are on and no learning has been written yet. The cron works on a schedule, a few
 * tests at a time, so there is no progress bar and no time estimate: only what is queued.
 */
export const WaitingView = ({ status }: WaitingViewProps) => (
  <div className="flex flex-col gap-[14px]">
    <div className="flex flex-wrap items-center justify-between gap-2.5">
      <StatusPill tone="accent">learnings: waiting on analysis</StatusPill>
      <SetupSteps current={2} done />
    </div>

    <section className="bg-panel border-line rounded-panel flex flex-col gap-3 overflow-hidden border px-[18px] pb-5 pt-[18px]">
      <span className="micro-label">Waiting on analysis</span>
      <h1 className="m-0 max-w-[40ch] text-[20px] font-medium leading-tight tracking-[-0.015em] text-balance">
        Your tests are waiting on analysis
      </h1>
      <p className="text-dim m-0 max-w-[62ch] text-pretty">
        This can take several hours. You can leave this page. Learnings appear here once analysis is done.
      </p>
      {status.paused && <p className="text-warn m-0 text-meta">{PAUSED[status.paused]}</p>}
    </section>

    <Section
      tourAnchor="learnings-queue"
      title="In the queue"
      description="Each test gets a debrief. Patterns are pooled from these once analysis is done."
    >
      {status.queue.map((test) => (
        <div
          key={test.experimentId}
          className="border-line grid items-center gap-3 border-b px-3.5 py-[9px] [grid-template-columns:64px_minmax(0,1fr)_auto]"
        >
          <span className="num text-dim">#{test.experimentId}</span>
          <span className="min-w-0 truncate text-meta font-medium">{test.name}</span>
          <span
            className={cn(
              MICRO_LABEL_SHAPE,
              'border-line text-dim inline-flex items-center gap-1.5 rounded-pill border px-2 py-0.5',
            )}
          >
            <Clock className="size-[11px]" strokeWidth={1.5} aria-hidden="true" />
            waiting
          </span>
        </div>
      ))}
      <div className="px-3.5 py-2">
        <span className="supporting">
          Showing {status.queue.length} of {status.pending} {status.pending === 1 ? 'test' : 'tests'}
        </span>
      </div>
    </Section>
  </div>
);
